(() => {
  const ACCEPT = 'accept:';

  if (customElements.get('custom-product-upsell')) return;

  customElements.define(
    'custom-product-upsell',
    class CustomProductUpsell extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();

        // Delegated so the picker keeps working on whichever slide the shopper scrolls to.
        this.addEventListener(
          'change',
          (event) => {
            const input = event.target.closest('[data-upsell-option]');
            if (!input) return;

            this.select(input.closest('.myb-upsell__slide'), Number(input.dataset.upsellOption), input.value);
          },
          { signal: this.abortController.signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
        // The dialog's close event cannot fire once the element is gone, so a caller awaiting a
        // choice would hang and leave its Add to cart button disabled for good.
        this.settle?.(null);
      }

      /**
       * Moves a slide to the add-on variant the shopper just chose a value for.
       *
       * @param {HTMLElement} slide - The slide the changed input belongs to.
       * @param {number} optionIndex - Position of the changed option within the variant's options.
       * @param {string} optionValue - The value the shopper picked.
       */
      select(slide, optionIndex, optionValue) {
        const variants = this.json(slide, '[data-upsell-variants]', []);
        const candidates = variants.filter((variant) => variant.options[optionIndex] === optionValue);
        if (!candidates.length) return;

        // The offer lists a hand-picked subset of a product's variants, so a combination the shopper
        // assembles may not exist; the fallback snaps the other options onto one that does.
        const variant = candidates.find((candidate) => this.matchesChecked(slide, candidate)) || candidates[0];

        slide.querySelectorAll('[data-upsell-option]').forEach((input) => {
          input.checked = input.value === variant.options[Number(input.dataset.upsellOption)];
        });

        slide.querySelectorAll('[data-upsell-selected-value]').forEach((label) => {
          label.textContent = variant.options[Number(label.dataset.upsellSelectedValue)];
        });

        const media = slide.querySelector('[data-upsell-image]');
        const source = this.json(slide, '[data-upsell-images]', {})[variant.image];
        if (media && source && media.dataset.upsellImage !== variant.image) {
          media.dataset.upsellImage = variant.image;
          this.showImage(media, source, variant.image);
        }

        const price = slide.querySelector('[data-upsell-price]');
        if (price) price.textContent = variant.price;

        const accept = slide.querySelector('[data-upsell-accept]');
        if (accept) accept.value = `${ACCEPT}${variant.id}`;
      }

      /**
       * Swaps the add-on photo once the new one has decoded, so the shopper never sees the gap a
       * bare src assignment leaves while it downloads.
       *
       * @param {HTMLElement} media - The wrapper holding the img and the id of what it is showing.
       * @param {{src: string, srcset: string}} source - The photo to move to.
       * @param {string} imageId - Checked again on arrival, in case a faster pick has overtaken this one.
       */
      showImage(media, source, imageId) {
        const image = media.querySelector('img');
        if (!image) return;

        const next = new Image();
        next.srcset = source.srcset;
        next.sizes = image.sizes;
        next.src = source.src;

        const apply = () => {
          if (media.dataset.upsellImage !== imageId) return;
          image.srcset = source.srcset;
          image.src = source.src;
        };

        next.decode().then(apply, apply);
      }

      json(slide, selector, fallback) {
        try {
          return JSON.parse(slide.querySelector(selector)?.textContent || '') ?? fallback;
        } catch (error) {
          console.error(error);
          return fallback;
        }
      }

      // Not `matches`: that shadows Element.prototype.matches, which third-party scripts call on
      // this element with a selector string.
      matchesChecked(slide, variant) {
        return Array.from(slide.querySelectorAll('[data-upsell-option]:checked')).every(
          (input) => variant.options[Number(input.dataset.upsellOption)] === input.value
        );
      }

      /**
       * Opens the offer and reports the shopper's choice. Adding is the product form's job: it
       * sends the add-on in the same cart request as the product, so the cart can never end up
       * holding the add-on alone.
       *
       * @returns {Promise<number|null>} The chosen variant id, or null when the offer is turned
       * down or dismissed — dismissing still adds the product, which Add to cart already asked for.
       */
      ask() {
        const dialog = this.querySelector('dialog');
        if (!dialog || dialog.open) return Promise.resolve(null);

        return new Promise((resolve) => {
          this.settle = (variantId) => {
            this.settle = null;
            resolve(variantId);
          };

          dialog.addEventListener(
            'close',
            () => {
              const choice = dialog.returnValue || '';
              if (!choice.startsWith(ACCEPT)) {
                this.settle?.(null);
                return;
              }

              const variantId = Number(choice.slice(ACCEPT.length));
              this.settle?.(variantId > 0 ? variantId : null);
            },
            { once: true }
          );

          dialog.returnValue = '';
          dialog.showModal();
        });
      }
    }
  );
})();
